<template>
  <div v-if="loaded">
    <v-row>
      <v-col xl="3" lg="6" md="6" cols="12">
        <v-card>
          <v-card-subtitle>
            {{ $tc('route', 2) }}
          </v-card-subtitle>
          <v-divider/>
          <v-list dense>
            <v-list-item
              v-for="(property, index) of getPropertiesBySection('route')"
              :key="index"
              :class="{ 'warning--text': property.hasProblem }">
              <v-list-item-icon>
                <v-icon v-if="property.hasProblem" color="warning">mdi-alert</v-icon>
                <v-icon v-else>mdi-check</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ property.label }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="property.html" v-html="property.html"/>
                  <span v-if="property.date">
                  (<multi-time-display class="d-inline" :time="property.date" time-format="MMM D, h:mma"/>)
                </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                {{ property.hasProblem ? $t('yes') : $t('no') }}
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col xl="3" lg="6" md="6" cols="12">
        <v-card>
          <v-card-subtitle>
            {{ $tc('stop', 2) }}
          </v-card-subtitle>
          <v-divider/>
          <v-list dense>
            <v-list-item
              v-for="(property, index) of getPropertiesBySection('stops')"
              :key="index"
              :class="{ 'warning--text': property.hasProblem }">
              <v-list-item-icon>
                <v-icon v-if="property.hasProblem" color="warning">mdi-alert</v-icon>
                <v-icon v-else>mdi-check</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ property.label }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="property.html" v-html="property.html"/>
                  <span v-if="property.date">
                  (<multi-time-display class="d-inline" :time="property.date" time-format="MMM D, h:mma"/>)
                </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                {{ property.hasProblem ? $t('yes') : $t('no') }}
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col xl="3" lg="6" md="6" cols="12">
        <v-card>
          <v-card-subtitle>
            {{ $tc('telemetry', 2) }}
          </v-card-subtitle>
          <v-divider/>
          <v-list dense>
            <v-list-item
              v-for="(property, index) of getPropertiesBySection('telemetry')"
              :key="index"
              :class="{ 'warning--text': property.hasProblem }">
              <v-list-item-icon>
                <v-icon v-if="property.hasProblem" color="warning">mdi-alert</v-icon>
                <v-icon v-else>mdi-check</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ property.label }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="property.html" v-html="property.html"/>
                  <span v-if="property.date">
                  (<multi-time-display class="d-inline" :time="property.date" time-format="MMM D, h:mma"/>)
                </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                {{ property.hasProblem ? $t('yes') : $t('no') }}
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col xl="3" lg="6" md="6" cols="12">
        <v-card>
          <v-card-subtitle>{{ $tc('product', 2) }}</v-card-subtitle>
          <v-divider/>
          <div v-for="(product, index) of routeProductProperties" :key="index">
            <v-subheader>{{ product.name }}</v-subheader>
            <v-list dense>
              <v-list-item :class="{ 'warning--text': product.start_temp_modified }">
                <v-list-item-icon>
                  <v-icon v-if="product.start_temp_modified" color="warning">mdi-alert</v-icon>
                  <v-icon v-else>mdi-check</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('start_temp_modified' )}}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="product.start_temp_modified" v-html="'(' + formatTemperature(product.original_start_temp) + ')'"/>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  {{ product.start_temp_modified ? $t('yes') : $t('no') }}
                </v-list-item-action>
              </v-list-item>
              <v-list-item :class="{ 'warning--text': product.start_temp_unusual }">
                <v-list-item-icon>
                  <v-icon v-if="product.start_temp_unusual" color="warning">mdi-alert</v-icon>
                  <v-icon v-else>mdi-check</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('start_temp_unusual' )}}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  {{ product.start_temp_unusual ? $t('yes') : $t('no') }}
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
  <div v-else>
    <div v-if="loading" class="text-center p-2">
      <v-progress-linear indeterminate/>
    </div>
    <div v-else-if="couldNotLoad" class="text-danger p-3">
      {{ $t('route_data_quality_view_ui.could_not_load_data') }}
    </div>
  </div>
</template>

<script>
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { filter, map, get } from 'lodash'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { library } from '@fortawesome/fontawesome-svg-core'
import RouteDataQualityRecord from '@/orm/models/RouteDataQualityRecord'
import RouteProductDataQualityRecord from '@/orm/models/RouteProductDataQualityRecord'
import TemperatureConverterUtil from '@/util/TemperatureConverterUtil'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'

library.add(faExclamationTriangle)

export default {
  name: 'RouteDataQualityView.vue',
  components: {
    MultiTimeDisplay
  },
  mixins: [
    ApiUtilitiesMixin,
    CurrentUserMixin
  ],
  props: {
    route: {
      type: Object
    }
  },
  data () {
    return {
      routeDataQuality: {},
      routeProductDataQualities: {},
      overlappingRoutes: [],
      loaded: false,
      couldNotLoad: false,
      loading: false,
      propertyTemplates: [
        {
          label: this.$t('unusual_duration'),
          section: 'route',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_DURATION_TOO_LONG) ||
              data.hasFlag(RouteDataQualityRecord.FLAG_DURATION_TOO_SHORT)
          }
        },
        {
          label: this.$t('overlapping_routes'),
          section: 'route',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_OVERLAPPING_ROUTE)
          },
          html: (data) => {
            if (data.hasFlag(RouteDataQualityRecord.FLAG_OVERLAPPING_ROUTE)) {
              return '(' + map(this.overlappingRoutes, overlappingRoute => {
                return `<a href="/route/${overlappingRoute.id}" target="_blank">${overlappingRoute.name}</a>`
              }).join(', ') + ')'
            }
          }
        },
        {
          label: this.$t('starts_after_dc_departure'),
          section: 'route',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_START_AFTER_DC_DEPARTURE)
          }
        },
        {
          label: this.$t('ends_before_last_known_stop'),
          section: 'route',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_END_BEFORE_LAST_KNOWN_STOP)
          }
        },
        {
          label: this.$t('user_modified_start_time'),
          section: 'route',
          problem: (data) => {
            return data.user_modified_start
          },
          date: (data) => {
            if (data.user_modified_start) {
              return data.original_start
            }
          }
        },
        {
          label: this.$t('user_modified_end_time'),
          section: 'route',
          problem: (data) => {
            return data.user_modified_end
          },
          date: (data) => {
            if (data.user_modified_end) {
              return data.original_end
            }
          }
        },
        {
          label: this.$t('no_deliveries'),
          section: 'stops',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_NO_DELIVERY_STOPS)
          }
        },
        {
          label: this.$t('has_missed_stops'),
          section: 'stops',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_MISSED_STOPS)
          }
        },
        {
          label: this.$tc('unplanned_stop', 2),
          section: 'stops',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_STOPPED_AT_UNPLANNED_LOCATION)
          }
        },
        {
          label: this.$t('cooler_sensor_failed'),
          section: 'telemetry',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_COOLER_SENSOR_FAILURE)
          }
        },
        {
          label: this.$t('freezer_sensor_failed'),
          section: 'telemetry',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_FREEZER_SENSOR_FAILURE)
          }
        },
        {
          label: this.$t('gps_sensor_failed'),
          section: 'telemetry',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_GPS_SENSOR_FAILURE)
          }
        },
        {
          label: this.$t('future_telemetry_reported'),
          section: 'telemetry',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_FUTURE_TELEMETRY)
          }
        },
        {
          label: this.$t('late_telemetry_reported'),
          section: 'telemetry',
          problem: (data) => {
            return data.hasFlag(RouteDataQualityRecord.FLAG_TELEMETRY_REPORTED_LATE)
          }
        }
      ]
    }
  },
  computed: {
    /**
     * Populate all route data quality properties via the property templates
     */
    properties () {
      return map(this.propertyTemplates, property => {
        let hasProblem
        const problemFunction = get(property, 'problem')
        if (problemFunction) {
          hasProblem = property.problem(this.routeDataQuality)
        }
        let html
        const htmlFunction = get(property, 'html')
        if (htmlFunction) {
          html = property.html(this.routeDataQuality)
        }
        let date
        const dateFunction = get(property, 'date')
        if (dateFunction) {
          date = property.date(this.routeDataQuality)
        }
        return {
          key: property.key,
          label: property.label,
          section: property.section,
          hasProblem: hasProblem,
          html: html,
          date: date
        }
      })
    },
    /**
     * Populate all properties needed for each route product.
     */
    routeProductProperties () {
      return map(this.route.route_products, routeProduct => {
        const routeProductDataQuality = this.routeProductDataQualities[routeProduct.id]
        return {
          name: routeProduct.product.name,
          start_temp_modified: routeProductDataQuality.hasFlag(RouteProductDataQualityRecord.FLAG_START_TEMPERATURE_MODIFIED),
          original_start_temp: routeProductDataQuality.original_start_temp,
          start_temp_unusual: routeProductDataQuality.hasFlag(RouteProductDataQualityRecord.FLAG_ABNORMAL_START_TEMPERATURE)
        }
      })
    }
  },
  methods: {
    /**
     * Retrieve all properties with a given section name
     */
    getPropertiesBySection (section) {
      return filter(this.properties, x => x.section === section)
    },
    /**
     * Retrieve the route and route product data quality from the API
     * @returns {Promise<void>}
     */
    async refreshResults () {
      try {
        this.loading = true
        // Fetch the route data quality
        const routeResponse = await this.$orm('RouteDataQuality').findAll({
          route_id: this.route.id
        })
        this.routeDataQuality = routeResponse.getItems()[0]

        // Fetch route product data qualities
        const routeProductResponse = await this.$orm('RouteProductDataQuality').findAll({
          route_product_id: map(this.route.route_products, x => x.id)
        })
        for (const item of routeProductResponse.getItems()) {
          this.routeProductDataQualities[item.route_product_id] = item
        }

        // Retrieve overlapping routes if necessary
        if (this.routeDataQuality.hasFlag(RouteDataQualityRecord.FLAG_OVERLAPPING_ROUTE)) {
          const result = await this.$orm('Route').findAll({
            vehicle_id: this.route.vehicle.id,
            customer_id: this.route.customer.id,
            end_after: this.route.start,
            start_before: this.route.end
          })
          this.overlappingRoutes = filter(result.getItems(), x => x.id !== this.route.id)
        }
        this.loaded = true
      } catch (error) {
        this.couldNotLoad = true
      } finally {
        this.loading = false
      }
    },
    /**
     * Format a temperature to the correct scale and precision
     */
    formatTemperature (temperature) {
      const temp = TemperatureConverterUtil.formatTemperature(
        TemperatureConverterUtil.to(temperature, 'C', this.customerContext.tempFormat())
      )
      return temp + '&deg;' + this.customerContext.tempFormat()
    }
  },
  mounted () {
    this.refreshResults()
  }
}
</script>

<style scoped>

</style>
